import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import BrandLogo from "@/components/Monogram";

const MENU_LINKS = [
  { label: "THE BLACK HOUSE STUDIO", to: "/" },
  { label: "THE BLACK HOUSE ESSENTIALS", to: "/" },
  { label: "THE BLACK HOUSE BASICS", to: "/" },
  { label: "CO-CREATE ARCHIVE", to: "/co-create" },
  { label: "ORDERS", to: "/orders" },
  { label: "ABOUT", to: "/about" },
  { label: "SHIPPING", to: "/shipping" },
  { label: "RETURNS", to: "/returns" },
  { label: "FAQ", to: "/faq" },
];

const MENU_LAYER_Z_INDEX = 2147483000;

export default function GlobalMenu({ className = "" }) {
  const [open, setOpen] = useState(false);
  const pointerHandledRef = useRef(false);

  useEffect(() => {
    const onDocumentClick = (event) => {
      const toggle = event.target?.closest?.('[data-global-menu-toggle="true"]');
      if (!toggle || toggle.dataset.menuOwner === "storefront") return;
      if (pointerHandledRef.current) return;
      event.preventDefault();
      event.stopPropagation();
      setOpen((value) => !value);
    };
    const onGlobalToggle = () => setOpen((value) => !value);
    const onGlobalOpen = () => setOpen(true);
    const onKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("click", onDocumentClick, true);
    window.addEventListener("tbh:toggle-menu", onGlobalToggle);
    window.addEventListener("tbh:open-menu", onGlobalOpen);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("click", onDocumentClick, true);
      window.removeEventListener("tbh:toggle-menu", onGlobalToggle);
      window.removeEventListener("tbh:open-menu", onGlobalOpen);
      window.removeEventListener("keydown", onKey);
    };
  }, []);

  const toggleMenu = () => setOpen((value) => !value);

  const onTogglePointerDown = (event) => {
    event.preventDefault();
    event.stopPropagation();
    pointerHandledRef.current = true;
    toggleMenu();
    window.setTimeout(() => {
      pointerHandledRef.current = false;
    }, 0);
  };

  const onToggleClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (pointerHandledRef.current) {
      pointerHandledRef.current = false;
      return;
    }
    toggleMenu();
  };

  const onToggleKeyDown = (event) => {
    if (!["Enter", " "].includes(event.key)) return;
    event.preventDefault();
    event.stopPropagation();
    toggleMenu();
  };

  const drawerPortal = typeof document !== "undefined"
    ? createPortal(
        <AnimatePresence>
          {open && (
            <motion.div
              key="global-menu-layer"
              initial={{ opacity: 1 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 1 }}
              className="global-menu-overlay fixed inset-0 bg-black flex flex-col"
              style={{ backgroundColor: "#000000", zIndex: MENU_LAYER_Z_INDEX }}
              onClick={() => setOpen(false)}
              data-testid="global-menu-drawer"
            >
              <button
                type="button"
                onClick={() => setOpen(false)}
                data-testid="global-menu-close"
                className="absolute top-5 right-5 text-white bg-black border border-[#1a1a1a] hover:border-[#d3d3d3] p-2"
                style={{ zIndex: MENU_LAYER_Z_INDEX + 20 }}
                aria-label="Close menu"
              >
                <X size={20} strokeWidth={1.4} />
              </button>
              <motion.div
                initial={{ x: "100%" }}
                animate={{ x: 0 }}
                exit={{ x: "100%" }}
                transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                onClick={(event) => event.stopPropagation()}
                className="ml-auto w-full sm:max-w-md h-full bg-black border-l border-[#141414] flex flex-col p-6 overflow-y-auto tbh-scroll"
                style={{ zIndex: MENU_LAYER_Z_INDEX + 10 }}
              >
                <div className="flex items-center justify-between">
                  <BrandLogo size={60} to="/" />
                </div>
                <p className="mt-10 font-mono-ui text-[9px] tracking-[0.35em] uppercase text-[#5a5a5a]">
                  — Navigation
                </p>
                <nav className="mt-5 space-y-3" aria-label="Global navigation">
                  {MENU_LINKS.map((link) => (
                    <Link
                      key={link.label}
                      to={link.to}
                      onClick={() => setOpen(false)}
                      data-testid={`global-menu-link-${link.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                      className="block w-full text-left font-display font-bold uppercase text-[14px] tracking-[0.12em] leading-[1.45] text-white hover:text-[#d3d3d3] transition-colors"
                    >
                      {link.label}
                    </Link>
                  ))}
                </nav>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body,
        "global-menu-layer"
      )
    : null;

  return (
    <>
      <button
        type="button"
        data-testid="global-menu-toggle"
        data-global-menu-toggle="true"
        aria-label="Menu"
        aria-expanded={open}
        onPointerDown={onTogglePointerDown}
        onClick={onToggleClick}
        onKeyDown={onToggleKeyDown}
        className={`global-menu-toggle font-mono-ui text-[9px] md:text-[10px] tracking-[0.16em] md:tracking-[0.28em] uppercase text-[#d3d3d3] border border-[#1a1a1a] px-2.5 md:px-3 py-2 whitespace-nowrap hover:border-[#d3d3d3] transition-colors ${className}`}
      >
        Menu
      </button>
      {drawerPortal}
    </>
  );
}